import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraVerificationInput } from "../../components/AtiraVerificationInput";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Text } from "../../components/Text";
import { TwoFactorMethod } from "../../model/user/types/TwoFactorMethod";
import { Checkbox } from "../../utils/local-antd";
import { useAppDispatch } from "../../redux/store";
import { userActions } from "../../redux/user/user.slice";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../utils/onSetTypedCRUDErrors";
import { TestIDs } from "../../tests/types/testIds";

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const LinkButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  color: ${(props) => props.theme.main};
  font-size: 0.8rem;
  margin: 0;
  padding: 0;
`;

type LoginTwoFactorForm = {
  code: string;
  trustDevice: boolean;
};

type LoginTwoFactorProps = {
  challengeToken: string;
  methods: TwoFactorMethod[];
  onVerified: VoidFunction;
  onBack: VoidFunction;
};

export const LoginTwoFactor: React.FC<LoginTwoFactorProps> = ({
  challengeToken,
  methods,
  onVerified,
  onBack,
}) => {
  const hasTotp = methods.includes(TwoFactorMethod.TOTP);
  const hasEmail = methods.includes(TwoFactorMethod.EMAIL);

  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [method, setMethod] = useState(
    hasTotp ? TwoFactorMethod.TOTP : TwoFactorMethod.EMAIL,
  );
  // Email-only logins get their code at step 1; with an authenticator linked
  // nothing is emailed until the user asks for it.
  const [emailSent, setEmailSent] = useState(!hasTotp);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { control, handleSubmit, getValues, setError, setValue, clearErrors } =
    useForm<LoginTwoFactorForm>({
      defaultValues: { code: "", trustDevice: false },
    });

  const onResendToken = async () => {
    await dispatch(userActions.resendLogin2faCode({ challengeToken })).unwrap();
  };

  const switchMethod = async () => {
    const next =
      method === TwoFactorMethod.TOTP
        ? TwoFactorMethod.EMAIL
        : TwoFactorMethod.TOTP;

    setValue("code", "");
    clearErrors("code");

    if (next === TwoFactorMethod.EMAIL && !emailSent) {
      try {
        setSwitching(true);
        await onResendToken();
        setEmailSent(true);
      } catch (e: any) {
        AtiraToast.apiError(e);
        console.log(e);
        return;
      } finally {
        setSwitching(false);
      }
    }

    setMethod(next);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      await dispatch(
        userActions.verifyLogin2fa({
          challengeToken,
          code: getValues("code"),
          trustDevice: getValues("trustDevice"),
        }),
      ).unwrap();

      onVerified();
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);
      onSetTypedCRUDErrors(e, setError);

      // Either the code was burned (5 wrong attempts) or the challenge expired -
      // nothing left to submit on this screen. The toast already tells the user
      // to sign in again; do it for them instead of leaving them stuck.
      // The API sends errors without `fields` as a plain string body.
      const data = e?.response?.data;
      const serverError = typeof data === "string" ? data : data?.message;

      if (
        serverError === "server-error/auth.2fa.too-many-attempts" ||
        serverError === "server-error/auth.2fa.invalid-challenge"
      ) {
        onBack();
      }
    } finally {
      setLoading(false);
    }
  };

  const isTotp = method === TwoFactorMethod.TOTP;

  return (
    <Flex
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width={"100%"}
      gap="s"
    >
      <Text color="gray" align="center">
        {isTotp ? t("login.twoFactor.totp.title") : t("login.twoFactor.title")}
      </Text>

      <Controller
        name="code"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.fieldRequired"),
          },
          minLength: {
            value: 6,
            message: t("common.error.verificationCode.length"),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <AtiraVerificationInput
            // Remount per method so the email resend cooldown starts when the
            // email is actually sent, not when the page first loaded.
            key={method}
            value={value}
            onChange={onChange}
            valid={!error}
            errorMessage={error?.message}
            onResendToken={isTotp ? undefined : onResendToken}
          />
        )}
      />

      {hasTotp && hasEmail ? (
        <LinkButton
          onClick={switchMethod}
          loading={switching}
          dataTestId={TestIDs.auth.login.twoFactor.methodSwitch}
          title={
            isTotp
              ? t("login.twoFactor.useEmailInstead")
              : t("login.twoFactor.useAuthenticatorInstead")
          }
        />
      ) : null}

      <Controller
        name="trustDevice"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Checkbox
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={TestIDs.auth.login.twoFactor.trustDevice}
          >
            <Text fontSize="s">{t("login.twoFactor.trustDevice")}</Text>
          </Checkbox>
        )}
      />

      <StyledButton
        onClick={handleSubmit(onSubmit)}
        loading={loading}
        dataTestId={TestIDs.auth.login.twoFactor.submit}
        title={t("login.twoFactor.button")}
      />

      <LinkButton
        onClick={onBack}
        title={t("common.back")}
        dataTestId={TestIDs.auth.login.twoFactor.back}
      />
    </Flex>
  );
};
